import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import * as StoreApi from "../../../api/storeApi";
import { getStoreControllerStatus } from "../../../api/storeApi";
import {
  emptyStore,
  Store,
  StoreOrderItem,
  StoreOrderState,
  toWriteModel
} from "../../../models/Store";
import { UserContext } from "../../../UserProvider";
import { Authenticated, hasRole } from "../../../models/User";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogContent,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Fade,
  FormControl,
  Hidden,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  Snackbar,
  TextField,
  useMediaQuery
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import useStyles from "./styles";
import { ActionStatuses, UpsertStoreState } from "./types";
import { StoreEditor } from "./components/StoreEditor";
import MaterialTable from "@material-table/core";
import { DoorOpenModal } from "../../../components/dialogs/DoorOpenModal";
import { thunkFetchStores } from "../../../store/stores/actions";
import { Alert, AlertTitle } from "@material-ui/lab";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { isOk } from "../../../result";
import { useTheme } from "@material-ui/core/styles";
import {
  Check,
  Clear,
  Edit,
  HelpOutline,
  HighlightOff,
  MeetingRoom,
  NotificationsOff,
  Sync,
  TabletAndroid,
  Timer
} from "@material-ui/icons";
import Typography from "@material-ui/core/Typography";
import StoreDetails from "./components/StoreDetails";
import { StoresState } from "../../../store/stores/types";
import { useNavigate, useParams } from "react-router-dom";

import { globalSettings } from "../../../models/Settings";

import * as StoreInventoryApi from "../../../api/storeInventoryApi";
import * as ProductApi from "../../../api/productApi";
import * as StoreOrderApi from "../../../api/storeOrderApi";
import { TenantProductEditor } from "../../tenants/Products/TenantProductEditor";
import { thunkFetchProductsV2 } from "../../../store/products/action";
import { emptyProduct } from "../../../models/Product";
import { SpoilageReasonSelect } from "../../../components/selects/SpoilageReasonSelect";
import { StoreOrderSelect } from "../ProductDelivery/StoreOrderSelect";
import { OrderUnit } from "../../../models/Order";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import Fuse from "fuse.js";
import { ConnectToPriceTag } from "../../tenants/Products/ConnectToPriceTag";
import { DropzoneArea } from "material-ui-dropzone";
import { useI18n } from "../../../hooks/useI18n";
import { ScanditBarcodeScanner } from "../../../components/ScanditBarcodeScanner";

const initialUpsertStoreState = (): UpsertStoreState => ({
  showDialog: false,
  newStore: false,
  store: emptyStore()
});

const getActionStatusInfo = (status: ActionStatuses): any => {
  switch (status) {
    case ActionStatuses.STOP_ALARM_SUCCESS:
      return { success: true, message: "The alarm has been stopped" };
    case ActionStatuses.STOP_ALARM_ERROR:
      return { success: false, message: "Failed to stop the alarm" };
    case ActionStatuses.UPDATE_PRICETAGS_SUCCESS:
      return { success: true, message: "Price tags are updated" };
    case ActionStatuses.UPDATE_PRICETAGS_ERROR:
      return { success: false, message: "Failed to update price tags" };
    case ActionStatuses.UPSERT_STORE_SUCCESS:
      return { success: true, message: "Store has been created/updated" };
    case ActionStatuses.OPEN_DOOR_SUCCESS:
      return { success: true, message: "Door is open" };
    default:
      return { success: false, message: "" };
  }
};

export const StoreList: FunctionComponent = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const classes = useStyles();
  const user = useContext(UserContext) as Authenticated;
  const params = useParams();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const listRef = useRef();
  const navigate = useNavigate();
  const isDistributorManager = hasRole(user, ["DistributorManager"]);
  const { t } = useI18n();

  const [state, setState] = useState<any>({
    uploading: false,
    upsertStore: initialUpsertStoreState(),
    showProductDelivery: false,
    storeId: null,
    displayConfirmUpdatePriceTags: false,
    actionStatus: 0,
    reloadControllerStatus: true,
    activeTabs: new Map(),
    productDelivery: { storeId: undefined, showDialog: false }
  });

  const [controllers, setControllers] = useState<any>([]);

  // We need this because material table mutates state
  const [localStores, setLocalStores] = useState([] as ReadonlyArray<Store>);

  const { stores } = useSelector<AppState, StoresState>(s => s.stores);

  console.log("Store Index");
  console.log(stores);

  useEffect(() => {
    return () =>
      // clean up Material Table state
      setDetailPanelState("", "");
  }, []);

  useEffect(() => {
    if (
      !stores ||
      !stores.length ||
      state.actionStatus === ActionStatuses.UPSERT_STORE_SUCCESS
    ) {
      dispatch(thunkFetchStores());
    }
  }, [state.actionStatus]);

  useEffect(() => {
    const fetchControllerStatuses = async (storeList: any) => {
      setState({ ...state, reloadControllerStatus: false });
      let controllerStatuses = [];
      for (let i = 0; i < storeList.length; i++) {
        let statusResponse = await getStoreControllerStatus(stores[i].id);
        controllerStatuses.push({
          storeId: stores[i].id,
          status:
            statusResponse.type === "OK" ? statusResponse.value : "Unknown"
        });
      }
      setControllers(controllerStatuses);
    };

    if (stores && stores.length) {
      setDetailPanelState(params.storeId ?? "", params.tab || "");

      if (state.reloadControllerStatus) {
        fetchControllerStatuses(stores);
      }

      if (params.storeId && !params.tab) editStoreAction(params.storeId);
    }
  }, [stores]);

  const upsertStoresRefreshHandler = (success: boolean) => {
    if (success) {
      setState({
        ...state,
        upsertStore: initialUpsertStoreState(),
        actionStatus: ActionStatuses.UPSERT_STORE_SUCCESS,
        reloadControllerStatus: state.upsertStore.newStore
      });
      navigate(".", { replace: true });
    } else {
      setState({
        ...state,
        upsertStore: { ...state.upsertStore, showDialog: true }
      });
    }
  };

  const setDoorOpenSuccessStatus = (success: boolean) => {
    if (success) {
      setState({
        ...state,
        showOpenDoor: false,
        actionStatus: ActionStatuses.OPEN_DOOR_SUCCESS
      });
    }
  };

  const openDoorAction = (storeId: string) => {
    setState({ ...state, showOpenDoor: true, storeId: storeId });
  };

  const stopAlarmAction = async (storeId: string) => {
    const result = await StoreApi.stopAlarm(storeId);
    if (result?.type === "ERROR") {
      setState({ ...state, actionStatus: ActionStatuses.STOP_ALARM_ERROR });
    } else {
      setState({ ...state, actionStatus: ActionStatuses.STOP_ALARM_SUCCESS });
    }
  };

  const updatePriceTagsAction = (storeId: string) => {
    setState({
      ...state,
      displayConfirmUpdatePriceTags: true,
      storeId: storeId
    });
  };

  const editStoreAction = (storeId: string) => {
    const store = stores.find(s => s.id === storeId);
    if (store != null) {
      setState({
        ...state,
        upsertStore: {
          store: toWriteModel(store, false),
          showDialog: true,
          newStore: false
        }
      });
    }
    navigate(`/stores/${storeId}`);
  };

  const setDetailPanelState = (storeId: string, tabId: string) => {
    if (storeId && tabId) {
      const m = state.activeTabs;
      m.set(storeId, tabId);
      setState({ ...state, activeTabs: m });
    }

    setLocalStores(
      stores.map(s => {
        if (s.id === storeId) {
          return {
            ...s,
            tableData: { showDetailPanel: detailPanel }
          };
        }
        if ("" === storeId) {
          return {
            ...s,
            tableData: { showDetailPanel: null }
          };
        }

        return s;
      })
    );
  };

  const detailPanel = (rowData: any) => {
    const store = stores.find(s => s.id === rowData.id);

    if (store != null) {
      return (
        <StoreDetails
          store={store}
          onTabSelect={(tab: string) => {
            const m = state.activeTabs;
            m.set(store.id, tab);
            setState({ ...state, activeTabs: m });
          }}
          activeTab={state.activeTabs.get(store.id)}
        />
      );
    }
  };

  const loadProduct = async (gtin: string) => {
    const result = await StoreInventoryApi.getSingleByGTIN(
      state.selectedStoreId,
      gtin
    );

    if (result.type === "OK") {
      const { value } = result;
      const { product } = value;
      setState({
        ...state,
        manualInput: gtin,
        deliveryItem: {
          productId: product.id,
          orderInfo: product.orderInfo,
          gtin: product.gtin,
          productName: product.name,
          brandName: product.brandName,
          size: product.details?.size,
          onShelfBeforeDelivery: value.onShelf,
          correctedOnShelf: value.onShelf,
          quantityDelivered:
            product.orderInfo?.distributionPackageSize ?? undefined
        },
        scanning: false,
        delivering: true
      });
    } else {
      if (result.details.status === 404) {
        if (result.details.detail === "ProductNotRegistered") {
          setState({
            ...state,
            scanning: false,
            manualInput: gtin,
            registeringInSystem: true
          });
        } else {
          setState({
            ...state,
            scanning: false,
            manualInput: gtin,
            registeringInStore: true
          });
        }
      } else {
        setState({
          ...state,
          getItemFailed: true,
          error: result.details.detail
        });
      }
    }
  };

  const totalQuantityDelivered = () => {
    const {
      deliveryItem: { correctedOnShelf, quantityDelivered }
    } = state;
    return (isNaN(correctedOnShelf) ? 0 : correctedOnShelf) + quantityDelivered;
  };

  const calculateOrderItemQuantityAfterDeliver = (item: StoreOrderItem) => {
    let total = 0;

    if (item.unit === OrderUnit.Kfp) {
      total += item.quantity;
    } else {
      total += (item.distributionPackageSize ?? 1) * item.quantity;
    }
    return (item.currentlyOnShelf ?? 0) + (isNaN(total) ? 0 : total);
  };

  const disabled = (item: StoreOrderItem) => {
    if (item.quantity === undefined) {
      return true;
    } else if (
      item.discrepancyQuantity &&
      item.discrepancyQuantity > 0 &&
      !item.discrepancyReasonId
    ) {
      return true;
    }
    return false;
  };

  const getOrderItems = () => {
    const {
      searchPhrase,
      orderToDeliver: { items }
    } = state;

    const searchOptions: any = {
      keys: ["searchMetaData"]
    };

    const all = items.map((item: StoreOrderItem) => ({
      ...item,
      searchMetaData: `${item.product.brandName}, ${item.product.brandName}, ${item.product.name}, ${item.product.articleNumber} ${item.product.gtin}`
    }));

    let fused: any =
      searchPhrase !== undefined && searchPhrase !== ""
        ? new Fuse(all, searchOptions).search(searchPhrase)
        : new Fuse(all, searchOptions);

    fused = fused.list ? fused.list : fused;

    return fused;
  };

  return (
    <>
      <MaterialTable
        title=""
        tableRef={listRef}
        isLoading={!localStores}
        columns={[
          {
            title: "Name",
            field: "name",
            render: (rowData: any) => {
              return (
                <div style={{ minWidth: 180 }}>
                  <Typography>{rowData.name}</Typography>
                  <Link
                    href={`https://maps.google.com/?q=${rowData.location.latitude},${rowData.location.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    title={t("common.actions.showLocation")}
                    variant={"body2"}
                    color="textSecondary"
                  >
                    {rowData.address.street}, {rowData.address.postalCode}{" "}
                    {rowData.address.city}
                  </Link>

                  {/* This part is for mobiles */}
                  <Hidden smUp>
                    <div style={{ whiteSpace: "nowrap" }}>
                      <IconButton
                        className={classes.iconButtonMobile}
                        title={t("common.actions.delivery")}
                        onClick={() => {
                          if (
                            user.tenant?.configuration?.features
                              ?.centralOrderingSystem
                          ) {
                            setState({
                              ...state,
                              chooseDeliveryType: true,
                              selectedStoreId: rowData.id,
                              selectedStoreName: rowData.name,
                              productDelivery: {
                                showDialog: true
                              }
                            });
                          } else {
                            setState({
                              ...state,
                              scanning: true,
                              selectedStoreId: rowData.id,
                              selectedStoreName: rowData.name,
                              productDelivery: {
                                showDialog: true
                              }
                            });
                          }
                        }}
                      >
                        <LocalShippingIcon />
                      </IconButton>
                      <IconButton
                        hidden={isDistributorManager}
                        className={classes.iconButtonMobile}
                        title={t("common.actions.openDoor")}
                        onClick={() => openDoorAction(rowData.id)}
                      >
                        <MeetingRoom />
                      </IconButton>
                      <IconButton
                        hidden={isDistributorManager}
                        className={classes.iconButtonMobile}
                        title={t("common.actions.stopAlarm")}
                        onClick={async () => await stopAlarmAction(rowData.id)}
                      >
                        <NotificationsOff />
                      </IconButton>
                      <IconButton
                        hidden={isDistributorManager}
                        className={classes.iconButtonMobile}
                        title={t("common.actions.updatePriceTags")}
                        onClick={() => updatePriceTagsAction(rowData.id)}
                      >
                        <Sync />
                      </IconButton>
                      <IconButton
                        hidden={isDistributorManager}
                        className={classes.iconButtonMobile}
                        title={t("common.actions.editStore")}
                        onClick={() => editStoreAction(rowData.id)}
                      >
                        <Edit />
                      </IconButton>
                    </div>
                  </Hidden>
                </div>
              );
            }
          },
          {
            title: "Opened",
            field: "state",
            hidden: useMediaQuery(theme.breakpoints.down("xs")),
            render: rowData => {
              return rowData.state === "Opened" ? (
                <Check className={classes.opened} titleAccess={rowData.state} />
              ) : (
                <Timer
                  className={classes.inSetup}
                  titleAccess={rowData.state}
                />
              );
            }
          },
          {
            title: "Online",
            field: "controllerStatus",
            render: rowData => {
              const controller = controllers.find(
                (x: any) => x.storeId === rowData.id
              );
              return !controller || !controller.status ? (
                <CircularProgress size={25} />
              ) : controller?.status === "Connected" ? (
                <Check
                  className={classes.opened}
                  titleAccess={controller?.status}
                />
              ) : controller?.status !== "Unknown" ? (
                <HighlightOff
                  className={classes.closed}
                  titleAccess={controller?.status}
                />
              ) : (
                <HelpOutline
                  className={classes.inSetup}
                  titleAccess={controller?.status}
                />
              );
            }
          },
          {
            title: "Id",
            field: "id",
            hidden: true,
            searchable: true
          }
        ]}
        data={localStores && localStores.length ? (localStores as []) : []}
        actions={[
          {
            icon: "add_circle",
            iconProps: {
              style: { color: theme.palette.primary.main, fontSize: "2rem" }
            },
            tooltip: "Add new store",
            hidden: !hasRole(user, ["Root"]),
            isFreeAction: true,
            onClick: () => {
              setState({
                ...state,
                upsertStore: {
                  ...state.upsertStore,
                  showDialog: true,
                  newStore: true
                }
              });
            }
          },
          {
            hidden: mobile,
            icon: "local_shipping",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: "Delivery",
            onClick: (event, rowData: any) => {
              if (user.tenant?.configuration?.features?.centralOrderingSystem) {
                setState({
                  ...state,
                  chooseDeliveryType: true,
                  selectedStoreId: rowData.id,
                  selectedStoreName: rowData.name,
                  productDelivery: {
                    showDialog: true
                  }
                });
              } else if (
                user.tenant?.configuration?.features?.deliveryThroughFileUpload
              ) {
                setState({
                  ...state,
                  uploadProductFile: true,
                  selectedStoreId: rowData.id,
                  selectedStoreName: rowData.name,
                  productDelivery: {
                    showDialog: true
                  }
                });
              } else {
                setState({
                  ...state,
                  scanning: true,
                  selectedStoreId: rowData.id,
                  selectedStoreName: rowData.name,
                  productDelivery: {
                    showDialog: true
                  }
                });
              }
            }
          },
          {
            hidden: mobile || isDistributorManager,
            icon: "meeting_room",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.openDoor"),
            onClick: (event, rowData: any) => openDoorAction(rowData.id)
          },

          {
            hidden: mobile || isDistributorManager,
            icon: "notifications_off",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.stopAlarm"),
            onClick: async (event, rowData: any) => {
              console.log(rowData);
              stopAlarmAction(rowData.id);
            }
          },
          {
            hidden: mobile || isDistributorManager,
            icon: "sync",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.updatePriceTags"),
            onClick: (event, rowData: any) => updatePriceTagsAction(rowData.id)
          },
          {
            icon: "edit",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editStore"),
            hidden: mobile || isDistributorManager,
            onClick: (event, rowData: any) => editStoreAction(rowData.id)
          }
        ]}
        detailPanel={detailPanel}
        options={{
          actionsColumnIndex: -1,
          detailPanelType: "single",
          actionsCellStyle: { whiteSpace: "nowrap" },
          pageSize: 15
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
      />

      <Dialog
        fullWidth
        fullScreen={mobile}
        maxWidth={"xl"}
        open={state.upsertStore.showDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle>
          {state.upsertStore.newStore
            ? t("common.actions.createStore")
            : t("common.actions.editStore")}
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, upsertStore: initialUpsertStoreState() });
              navigate(".", { replace: true });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent dividers>
          {state.upsertStore.newStore ? (
            <StoreEditor
              currentUser={user as Authenticated}
              store={emptyStore()}
              isSuccessHandler={upsertStoresRefreshHandler}
            />
          ) : (
            <StoreEditor
              currentUser={user as Authenticated}
              store={state.upsertStore.store}
              isSuccessHandler={upsertStoresRefreshHandler}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth
        fullScreen={mobile}
        maxWidth={"sm"}
        open={state.searchShowScanner}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, searchShowScanner: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent
          style={
            mobile
              ? { padding: 0, marginTop: 15 }
              : { marginTop: 15, marginBottom: 30 }
          }
        >
          <div
            className={classes.scanner}
            style={mobile ? { display: "flex", height: "70%" } : {}}
          >
            {state.searchShowScanner && (
              <ScanditBarcodeScanner
                licenseKey={process.env.REACT_APP_SCANDIT_LICENSE!}
                libraryLocation="https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.0.0/sdc-lib/"
                onScan={(result: any) => {
                  const { barcodes } = result;
                  if (barcodes.length > 0) {
                    const barcodeData = barcodes[0].data;
                    setState({
                      ...state,
                      searchPhrase: barcodeData,
                      searchShowScanner: false
                    });
                  }
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        open={state.productDelivery.showDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                manualInput: undefined,
                productDelivery: { showDialog: false, storeId: undefined },
                showScanner: false,
                scanning: undefined,
                delivering: undefined,
                deliverySuccess: undefined,
                registeringInStore: undefined,
                registeringInSystem: undefined,
                showRegisterProduct: undefined,
                registeringSuccess: undefined,
                registerFailed: false,
                chooseDeliveryType: false,
                file: undefined,
                hasProductDeliveryResult: false,
                productDeliveryFailed: undefined,
                deliveryResult: undefined
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        {state.chooseDeliveryType && (
          <DialogContent>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <FormControl>
                <InputLabel id="delivery-source-select-label">
                  Select delivery source
                </InputLabel>
                <Select
                  labelId="delivery-select-source"
                  style={
                    state.deliverySource === "Distributor"
                      ? { width: 200, marginRight: 30 }
                      : { width: 300 }
                  }
                  value={state?.deliverySource ?? ""}
                  onChange={event => {
                    setState({
                      ...state,
                      deliverySource: event.target.value,
                      orderToDeliver: undefined
                    });
                  }}
                >
                  <MenuItem value="Distributor">Distributor</MenuItem>
                  {!hasRole(user, ["DistributorManager"]) && (
                    <MenuItem value="Order">Order</MenuItem>
                  )}
                </Select>
              </FormControl>

              {state.deliverySource === "Distributor" && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    setState({
                      ...state,
                      showScanner: true,
                      chooseDeliveryType: false,
                      scanning: true
                    });
                  }}
                >
                  Scan
                </Button>
              )}
            </div>

            {state.deliverySource === "Order" && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: 15
                }}
              >
                <StoreOrderSelect
                  className={classes.select}
                  storeId={state.selectedStoreId}
                  onSelected={async order => {
                    setState({ ...state, isLoadingOrder: true });
                    const result = await StoreOrderApi.getOrder(order.id);
                    if (result.type === "OK") {
                      setState({
                        ...state,
                        isLoadingOrder: false,
                        orderToDeliver: {
                          ...result.value,
                          items: result.value.items
                            .filter(i => !i.delivered)
                            .map(i => {
                              i.productId = i.product.id;
                              i.currentlyOnShelf = i.onShelfBeforeDelivery;
                              return i;
                            })
                        }
                      });
                    }
                  }}
                  value={state.orderToDeliver?.id ?? ""}
                />
              </div>
            )}

            {state &&
              state.deliverySource === "Order" &&
              state?.isLoadingOrder && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginLeft: 10,
                    marginRight: 10,
                    marginTop: 25
                  }}
                >
                  <CircularProgress />
                </div>
              )}

            {state && state.deliverySource === "Order" && state.orderToDeliver && (
              <div style={{ marginLeft: 10, marginRight: 10, marginTop: 25 }}>
                {state.orderToDeliver.items.length > 0 ? (
                  <div>
                    <h3>{t("common.messages.orderContainsFollowingItems")}</h3>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <TextField
                        id="search-field"
                        className={classes.searchField}
                        value={state.searchPhrase}
                        placeholder={"Input search phrase"}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position={"end"}>
                              <Clear
                                style={{
                                  color: "rgba(0, 0, 0, 0.44)",
                                  cursor: "pointer"
                                }}
                                fontSize={"small"}
                                onClick={() =>
                                  setState({
                                    ...state,
                                    searchPhrase: ""
                                  })
                                }
                              />
                            </InputAdornment>
                          )
                        }}
                        onChange={e => {
                          setState({ ...state, searchPhrase: e.target.value });
                        }}
                      />
                      <Button
                        style={{ marginLeft: 10 }}
                        variant={"contained"}
                        color={"primary"}
                        startIcon={<TabletAndroid />}
                        onClick={() =>
                          setState({
                            ...state,
                            searchShowScanner: true
                          })
                        }
                      >
                        Scan
                      </Button>
                    </div>
                    {getOrderItems().map((item: StoreOrderItem) => {
                      return (
                        <div
                          style={{
                            marginBottom: 20
                          }}
                        >
                          <div style={{ marginBottom: 30 }}>
                            <Typography>{item.product.name}</Typography>
                            <Typography color="textSecondary">
                              {item.product.gtin}, {item.product.size}{" "}
                              {item.product.brandName}
                            </Typography>
                            <div>
                              <TextField
                                type="number"
                                className={classes.orderItemField}
                                label={t("common.formFields.currentlyOnShelf")}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      st
                                    </InputAdornment>
                                  )
                                }}
                                value={item?.currentlyOnShelf}
                                helperText={`According to system: ${item.onShelfBeforeDelivery}`}
                                onChange={e => {
                                  const value = parseInt(e.target.value);
                                  setState({
                                    ...state,
                                    orderToDeliver: {
                                      ...state.orderToDeliver,
                                      items: state.orderToDeliver.items.map(
                                        (i: any) => {
                                          if (
                                            i.product.id === item.product.id
                                          ) {
                                            i.currentlyOnShelf = isNaN(value)
                                              ? undefined
                                              : value;

                                            if (
                                              value != i.onShelfBeforeDelivery
                                            ) {
                                              i.discrepancyQuantity = Math.abs(
                                                (isNaN(value) ? 0 : value) -
                                                  i.onShelfBeforeDelivery
                                              );
                                            } else {
                                              i.discrepancyQuantity = undefined;
                                            }
                                          }
                                          return i;
                                        }
                                      )
                                    }
                                  });
                                }}
                              />
                              {item?.discrepancyQuantity &&
                                item.discrepancyQuantity > 0 && (
                                  <div style={{ marginTop: 10 }}>
                                    <Alert severity="warning">
                                      {`Discrepancy of ${item.discrepancyQuantity} items detected. Select a reason`}
                                    </Alert>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center"
                                      }}
                                    >
                                      <div style={{ width: "100%" }}>
                                        <SpoilageReasonSelect
                                          className={classes.field}
                                          onChange={event => {
                                            setState({
                                              ...state,
                                              orderToDeliver: {
                                                ...state.orderToDeliver,
                                                items:
                                                  state.orderToDeliver.items.map(
                                                    (i: any) => {
                                                      if (
                                                        i.product.id ==
                                                        item.product.id
                                                      ) {
                                                        i.discrepancyReasonId =
                                                          event.target.value;
                                                      }
                                                      return i;
                                                    }
                                                  )
                                              }
                                            });
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              <TextField
                                className={classes.orderItemField}
                                label="Delivering"
                                type="number"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {item.unit === OrderUnit.Kfp
                                        ? "ST/KFP"
                                        : `DFP (${
                                            item.distributionPackageSize ??
                                            "..."
                                          } st)`}
                                    </InputAdornment>
                                  )
                                }}
                                value={item?.quantity}
                                onChange={e => {
                                  const value = parseInt(e.target.value);

                                  setState({
                                    ...state,
                                    orderToDeliver: {
                                      ...state.orderToDeliver,
                                      items: state.orderToDeliver.items.map(
                                        (i: any) => {
                                          if (i.product.id == item.product.id) {
                                            i.quantity = isNaN(value)
                                              ? undefined
                                              : value;
                                          }
                                          return i;
                                        }
                                      )
                                    }
                                  });
                                }}
                              />
                              <h3>{`Total on shelf after delivery: ${calculateOrderItemQuantityAfterDeliver(
                                item
                              )}`}</h3>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  marginTop: 20
                                }}
                              >
                                <Button
                                  style={{ width: 200 }}
                                  disabled={disabled(item)}
                                  variant="contained"
                                  color="primary"
                                  onClick={async () => {
                                    const result =
                                      await StoreOrderApi.deliverItem(
                                        state.selectedStoreId,
                                        state.orderToDeliver.id,
                                        item
                                      );
                                    if (result.type === "OK") {
                                      const itemsLeftToDeliver =
                                        state.orderToDeliver.items.filter(
                                          (i: any) => i.id !== item.id
                                        );

                                      setState({
                                        ...state,
                                        saveItemSuccess: true,
                                        deliverySource:
                                          itemsLeftToDeliver.length > 0
                                            ? state.deliverySource
                                            : undefined,
                                        orderToDeliver:
                                          itemsLeftToDeliver.length > 0
                                            ? {
                                                ...state.orderToDeliver,
                                                items: itemsLeftToDeliver
                                              }
                                            : undefined
                                      });

                                      if (itemsLeftToDeliver.length === 0) {
                                        StoreOrderApi.updateOrder(
                                          state.orderToDeliver.id,
                                          StoreOrderState.Delivered
                                        );
                                      }
                                    } else {
                                      setState({
                                        ...state,
                                        saveItemFailed: true,
                                        error: result.details.detail
                                      });
                                    }
                                  }}
                                >
                                  {state.orderToDeliver.items.length > 1
                                    ? t("common.actions.deliverItem")
                                    : t("common.actions.deliverLastItem")}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div>
                    <h3>
                      All the items in this order has been marked as delivered
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 20
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                          const result = StoreOrderApi.updateOrder(
                            state.orderToDeliver.id,
                            StoreOrderState.Delivered
                          );
                        }}
                      >
                        Mark order as delivered
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        )}

        {state.scanning && (
          <DialogContent
            style={mobile ? { padding: 0, marginTop: 15 } : { marginTop: 15 }}
          >
            <div
              className={classes.scanner}
              style={mobile ? { display: "flex", height: "70%" } : {}}
            >
              <ScanditBarcodeScanner
                licenseKey={globalSettings.settings.scandit.scannerLicenseKey}
                engineLocation={globalSettings.settings.scandit.scannerEngine}
                guiStyle={globalSettings.settings.scandit.guiStyle}
                paused={false}
                accessCamera={true}
                onReady={() => {}}
                scanSettings={globalSettings.settings.scandit.scanSettings}
                onScan={async (result: ScanResult) => {
                  const { barcodes } = result;
                  if (barcodes.length > 0) {
                    await loadProduct(barcodes[0].data);
                  }
                }}
                onScanError={() => {
                  debugger;
                }}
              />
            </div>
            <TextField
              style={{ width: "100%", marginTop: 10 }}
              type="number"
              label={t("common.formFields.manualInput")}
              value={state.manualInput}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={async () => {
                        if (state.manualInput) {
                          await loadProduct(state.manualInput);
                        }
                      }}
                    >
                      OK
                    </Button>
                  </InputAdornment>
                )
              }}
              onChange={e => {
                setState({ ...state, manualInput: e.target.value });
              }}
            />
            {state.getItemFailed && (
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Alert severity="error">{state.error}</Alert>
              </div>
            )}
          </DialogContent>
        )}

        {state.uploadProductFile && (
          <DialogContent
            style={mobile ? { padding: 0, marginTop: 15 } : { marginTop: 15 }}
          >
            <div>
              <DropzoneArea
                acceptedFiles={[
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  ".xlsx"
                ]}
                dropzoneText={"Drag and drop a file to re-stock products"}
                showPreviewsInDropzone={true}
                useChipsForPreview
                filesLimit={1}
                onChange={files => {
                  setState({
                    ...state,
                    file: files[files.length - 1],
                    uploaded: false,
                    result: null
                  });
                }}
              />
            </div>
            {state.file ? (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 20
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      setState({ ...state, uploading: true });
                      const result = await StoreInventoryApi.reportDeliveryFile(
                        state.selectedStoreId,
                        state.file
                      );
                      setState({ ...state, uploading: false });
                      if (isOk(result)) {
                        setState({
                          ...state,
                          hasProductDeliveryResult: true,
                          productDeliveryResult: result.value
                        });
                      } else {
                        setState({
                          ...state,
                          deliveryFailed: true,
                          deliveryResult: result.details
                        });
                      }
                    }}
                  >
                    Upload file
                  </Button>
                </div>
              </>
            ) : null}
            {state.deliveryFailed && (
              <Alert severity="error" style={{ marginTop: 20 }}>
                <AlertTitle>
                  {state.deliveryResult.status}, {state.deliveryResult.title}
                </AlertTitle>
                {state.deliveryResult.detail}{" "}
                <strong>{state.deliveryResult.traceId}</strong>
              </Alert>
            )}
            {state.hasProductDeliveryResult && (
              <>
                <ExpansionPanel variant={"outlined"} style={{ marginTop: 20 }}>
                  <ExpansionPanelSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="output-content"
                    id="output-header"
                  >
                    <Typography>Output</Typography>
                  </ExpansionPanelSummary>
                  <ExpansionPanelDetails>
                    <List>
                      {state.productDeliveryResult.map((x: any) => (
                        <ListItem key={x}>
                          <ListItemText primary={x} />
                        </ListItem>
                      ))}
                    </List>
                  </ExpansionPanelDetails>
                </ExpansionPanel>
              </>
            )}
          </DialogContent>
        )}

        {state.registeringInStore && (
          <DialogContent
            style={
              mobile
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <Alert
                severity="warning"
                style={{ display: "flex", justifyContent: "center" }}
              >
                This product is not registered in{" "}
                <b>{state.selectedStoreName}</b>.
                {user.tenant?.configuration?.features?.digitalPriceTags
                  ? ` Click "Connect to price tag" or "Activate" to activate the product`
                  : ` Click "Activate" to activate the product`}
              </Alert>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                {user.tenant?.configuration?.features?.digitalPriceTags && (
                  <>
                    <Button
                      color="primary"
                      startIcon={<Link />}
                      variant="contained"
                      onClick={() =>
                        setState({
                          ...state,
                          showConnectToPriceTag: true,
                          storeId: state.selectedStoreId
                        })
                      }
                    >
                      Connect to price tag
                    </Button>
                    <Typography
                      variant={"body1"}
                      style={{
                        marginLeft: 15,
                        marginRight: 15,
                        alignSelf: "center"
                      }}
                    >
                      OR
                    </Typography>
                  </>
                )}

                <Button
                  style={{ width: 200 }}
                  disabled={state?.registeringProduct === true}
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    setState({ ...state, registeringProduct: true });
                    const result = await StoreInventoryApi.add(
                      state.selectedStoreId,
                      { gtin: state.manualInput }
                    );
                    if (result.type === "OK") {
                      setState({
                        ...state,
                        registeringInStore: false,
                        registeringProduct: false,
                        registeringSuccess: true
                      });
                    } else {
                      setState({
                        ...state,
                        registerFailed: true,
                        error: result.details.detail
                      });
                    }
                  }}
                >
                  Activate{" "}
                  {state?.registeringProduct === true && (
                    <CircularProgress size={20} style={{ marginLeft: 20 }} />
                  )}
                </Button>
              </div>
              {state.registerFailed && (
                <div
                  style={{
                    display: "flex",
                    marginTop: 20,
                    justifyContent: "center"
                  }}
                >
                  <Alert severity="error">{state.error}</Alert>
                </div>
              )}
            </div>
          </DialogContent>
        )}
        {state.registeringInSystem && (
          <DialogContent
            style={
              mobile
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <Alert
                severity="warning"
                style={{ display: "flex", justifyContent: "center" }}
              >
                This product is not registered in the system
              </Alert>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Button
                  style={{ width: 200 }}
                  disabled={state?.registeringProduct === true}
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    setState({ ...state, registeringProduct: true });
                    const result = await ProductApi.lookup(state.manualInput);
                    if (result.type === "OK") {
                      setState({
                        ...state,
                        productToRegisterInSystem: result.value.product ?? {
                          ...emptyProduct(),
                          gtin: state.manualInput
                        },
                        productImage: result.value.imageData,
                        showRegisterProduct: true,
                        registeringInSystem: false,
                        registeringProduct: false
                      });
                    } else {
                      setState({
                        ...state,
                        productToRegisterInSystem: {
                          ...emptyProduct(),
                          gtin: state.manualInput
                        },
                        showRegisterProduct: true,
                        registeringInSystem: false,
                        registeringProduct: false
                      });
                    }
                  }}
                >
                  Register{" "}
                  {state?.registeringProduct === true && (
                    <CircularProgress size={20} style={{ marginLeft: 20 }} />
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
        {state.showRegisterProduct && (
          <DialogContent
            style={
              mobile
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <TenantProductEditor
                adding={false}
                hasAlternatives={false}
                alternativeTypes={[]}
                alternativeInstances={[]}
                editingProduct={state.productToRegisterInSystem}
                image={state.productImage}
                onSave={async () => {
                  dispatch(thunkFetchProductsV2());
                  const result = await StoreInventoryApi.add(
                    state.selectedStoreId,
                    { gtin: state.manualInput }
                  );
                  if (result.type === "OK") {
                    setState({
                      ...state,
                      registeringInSystem: false,
                      showRegisterProduct: false,
                      registeringSuccess: true
                    });
                  }
                }}
              />
            </div>
          </DialogContent>
        )}
        {state.registeringSuccess && (
          <DialogContent
            style={
              mobile
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <Alert severity="success">
                The product has been registered in{" "}
                <b>{state.selectedStoreName}</b>. Click the button below to
                start the scanner again
              </Alert>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Button
                  style={{ width: 200 }}
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    setState({
                      ...state,
                      registeringSuccess: false,
                      scanning: true,
                      manualInput: undefined
                    })
                  }
                >
                  Start scanning
                </Button>
              </div>
            </div>
          </DialogContent>
        )}

        {state.delivering && (
          <DialogContent
            style={
              mobile
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <Typography>{state.deliveryItem.productName}</Typography>
              <Typography color="textSecondary">
                {state.deliveryItem.gtin}, {state.deliveryItem.size}{" "}
                {state.deliveryItem.brandName}
              </Typography>
            </div>
            <TextField
              className={classes.field}
              label={t("common.formFields.currentlyOnShelf")}
              type="number"
              value={state?.deliveryItem?.correctedOnShelf}
              helperText={`According to system: ${state.deliveryItem.onShelfBeforeDelivery}`}
              onChange={e => {
                const value = parseInt(e.target.value);

                const {
                  deliveryItem: { onShelfBeforeDelivery }
                } = state;

                let discrepancyQuantity = undefined;

                if (value != onShelfBeforeDelivery) {
                  discrepancyQuantity = Math.abs(
                    (isNaN(value) ? 0 : value) - onShelfBeforeDelivery
                  );
                }

                setState({
                  ...state,
                  discrepancyItem:
                    discrepancyQuantity === undefined
                      ? undefined
                      : {
                          ...state.discrepancyItem,
                          quantity: discrepancyQuantity
                        },
                  deliveryItem: {
                    ...state.deliveryItem,
                    correctedOnShelf: isNaN(value) ? undefined : value
                  }
                });
              }}
            />
            {state.discrepancyItem &&
              state.discrepancyItem.quantity &&
              state.discrepancyItem.quantity > 0 && (
                <div style={{ marginTop: 10 }}>
                  <Alert severity="warning">
                    {`Discrepancy of ${state.discrepancyItem.quantity} items detected. Select a reason and click Save`}
                  </Alert>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ width: "100%" }}>
                      <SpoilageReasonSelect
                        className={classes.field}
                        onChange={event => {
                          setState({
                            ...state,
                            discrepancyItem: {
                              ...state.discrepancyItem,
                              reasonId: event.target.value
                            }
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            <TextField
              className={classes.field}
              type="number"
              label={t("common.formFields.deliveredToShelf")}
              value={state?.deliveryItem?.quantityDelivered}
              onChange={e => {
                const value = parseInt(e.target.value);
                setState({
                  ...state,
                  deliveryItem: {
                    ...state.deliveryItem,
                    quantityDelivered: isNaN(value) ? undefined : value
                  }
                });
              }}
            />
            {state.deliveryItem &&
              state?.deliveryItem?.quantityDelivered > 0 && (
                <h3>{`Total on shelf after delivery: ${totalQuantityDelivered()}`}</h3>
              )}

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 20
              }}
            >
              <Button
                style={{ width: 200 }}
                variant="contained"
                color="primary"
                disabled={
                  state.deliveryItem.quantityDelivered == undefined ||
                  (state?.deliveryItem?.quantityDelivered === 0 &&
                    state?.discrepancyItem?.quantity > 0 &&
                    !state.discrepancyItem.reasonId)
                }
                onClick={async () => {
                  const {
                    deliveryItem: {
                      productId,
                      onShelfBeforeDelivery,
                      correctedOnShelf,
                      quantityDelivered
                    }
                  } = state;

                  const { discrepancyItem } = state || {};
                  const { quantity, reasonId } = discrepancyItem || {};

                  const result = await ProductApi.reportDelivery({
                    source: { type: "distributor" },
                    destination: {
                      type: "store",
                      id: state.selectedStoreId
                    },
                    item: {
                      productId: productId,
                      onShelfQuantityBeforeDelivery:
                        correctedOnShelf ?? onShelfBeforeDelivery,
                      onShelfQuantityDelivered: quantityDelivered
                    },
                    spoilageReport:
                      quantity && reasonId
                        ? { quantity, comment: "", reasonId: reasonId }
                        : undefined
                  });
                  if (result.type === "OK") {
                    setState({
                      ...state,
                      deliverySuccess: true,
                      discrepancyItem: undefined,
                      delivering: false
                    });
                  } else {
                    setState({
                      ...state,
                      deliveryFailed: true,
                      error: result.details.detail
                    });
                  }
                }}
              >
                Save
              </Button>
            </div>
            {state.deliveryFailed && (
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Alert severity="error">{state.error}</Alert>
              </div>
            )}
          </DialogContent>
        )}
        {state.deliverySuccess && (
          <DialogContent>
            <div>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Alert severity="success">
                  {t("common.messages.itemDelivered")}
                </Alert>
              </div>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Button
                  style={{
                    width: 200
                  }}
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    setState({
                      ...state,
                      scanning: true,
                      delivering: false,
                      deliverySuccess: false,
                      item: undefined,
                      manualInput: undefined
                    });
                  }}
                >
                  Scan more items
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <ConfirmDialog
        visible={state.displayConfirmUpdatePriceTags}
        titleText={"Are you sure?"}
        confirmText={"Yes"}
        cancelText={"No"}
        onCancel={() =>
          setState({
            ...state,
            displayConfirmUpdatePriceTags: false,
            storeId: null
          })
        }
        onConfirm={async () => {
          if (!state.displayConfirmUpdatePriceTags && state.storeId === null) {
            return true;
          }

          const result = await StoreApi.syncAllPriceTags(state.storeId ?? "");

          setState({
            ...state,
            displayConfirmUpdatePriceTags: false,
            storeId: state.storeId
          });

          setState({
            ...state,
            actionStatus: isOk(result)
              ? ActionStatuses.UPDATE_PRICETAGS_SUCCESS
              : ActionStatuses.UPDATE_PRICETAGS_ERROR
          });

          return true;
        }}
      >
        Do you want to re-sync all digital price tags in{" "}
        <strong>
          {!stores.length ? "" : stores.find(s => s.id === state.storeId)?.name}
        </strong>
        ?
      </ConfirmDialog>

      {stores && stores.length > 0 && (
        <DoorOpenModal
          visible={state.showOpenDoor}
          store={stores.find(s => s.id === state.storeId)}
          onCancel={() => setState({ ...state, showOpenDoor: false })}
          isSuccessHandler={setDoorOpenSuccessStatus}
        />
      )}

      <Snackbar
        id={"priceTags"}
        open={state.actionStatus > 0}
        autoHideDuration={3000}
        onClose={() => {
          setState({
            ...state,
            actionStatus: 0,
            displayConfirmUpdatePriceTags: false,
            storeId: null
          });
        }}
      >
        {state.actionStatus > 0 ? (
          <Alert
            severity={
              getActionStatusInfo(state.actionStatus).success
                ? "success"
                : "error"
            }
          >
            {getActionStatusInfo(state.actionStatus).message}
          </Alert>
        ) : (
          <div />
        )}
      </Snackbar>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        open={state.showConnectToPriceTag}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, showConnectToPriceTag: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <ConnectToPriceTag
            addToStore={true}
            storeId={state.selectedStoreId}
            gtin={state.manualInput}
            onSuccess={() =>
              setState({
                ...state,
                showConnectToPriceTag: false,
                registeringSuccess: true,
                registeringInStore: false
              })
            }
            onCancel={() =>
              setState({
                ...state,
                showConnectToPriceTag: false,
                storeId: ""
              })
            }
          />
        </DialogContent>
      </Dialog>

      <Modal
        open={state.uploading}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Fade in={state.uploading}>
          <Card>
            <CardHeader title={""} titleTypographyProps={{ variant: "h5" }} />
            <CardContent>
              <CircularProgress />
            </CardContent>
          </Card>
        </Fade>
      </Modal>
    </>
  );
};
